/**
 * 로컬 임시 저장소 (IndexedDB).
 * 청첩장 편집 중인 내용을 서버에 저장하기 전까지 브라우저에 안전하게 보관한다.
 * → 창을 최소화하거나, 새로고침/종료 후 다시 들어와도 작성하던 내용이 유지된다.
 */

const DB_NAME = 'wedding-draft-db';
const STORE = 'drafts';
const VERSION = 1;

export interface InvitationDraft {
  ownerId: string;
  data: unknown;
  theme: string;
  published: boolean;
  savedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'ownerId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Failed to open draft store'));
  });
}

export async function readDraft(ownerId: string): Promise<InvitationDraft | null> {
  if (!ownerId) return null;
  try {
    const db = await openDb();
    return await new Promise<InvitationDraft | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(ownerId);
      req.onsuccess = () => resolve((req.result as InvitationDraft | undefined) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function writeDraft(draft: InvitationDraft): Promise<void> {
  if (!draft.ownerId) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(draft);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* 임시 저장은 최선 노력(best-effort) */
  }
}

export async function clearDraft(ownerId: string): Promise<void> {
  if (!ownerId) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(ownerId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* 임시 저장은 최선 노력(best-effort) */
  }
}